import { RadioGroup } from "@headlessui/react";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as settingsApi from "../api";
import toast from "react-hot-toast";
import { useUserPreferences } from "../hooks/useUserPreferences";
import { useWatchlistAlertMode, WatchlistAlertMode } from "../hooks/useWatchlistAlertMode";
import { useNotificationSoundSettings } from "../hooks/useNotificationSoundSettings";
import { useBrowserNotificationSettings } from "../hooks/useBrowserNotificationSettings";
import { useNotificationPermission } from "../hooks/useNotificationPermission";
import {
  isBrowserNotificationSupported,
  requestNotificationPermission,
} from "@/utils/browser-notification-utils";
import ChannelSoundPicker from "../components/ChannelSoundPicker";
import SettingToggleRow from "../components/SettingToggleRow";
import TruthSocialNotificationControls from "../components/TruthSocialNotificationControls";

// Shared section wrapper — title+description in the left column, controls on the
// right. Same grid every settings tab uses, with a tighter vertical rhythm.
const SECTION =
  "grid max-w-7xl grid-cols-1 gap-x-8 gap-y-6 px-4 py-8 sm:px-6 md:grid-cols-3 lg:px-8";
const CONTROL_COL = "self-center md:col-span-2 space-y-4";

const alertModeOptions: { value: WatchlistAlertMode; label: string; description: string }[] = [
  { value: "off", label: "Off", description: "No watchlist sound alerts" },
  { value: "highlight", label: "Highlight only", description: "Alert when items match highlighted watchlists" },
  { value: "filter", label: "Filter only", description: "Alert when items match filtered watchlists" },
  { value: "both", label: "Both", description: "Alert for both filter and highlight" },
];

function Notifications() {
  const { notificationsSound } = useUserPreferences();
  const { watchlistAlertMode, setWatchlistAlertMode } = useWatchlistAlertMode();
  const { settings: soundSettings, updateChannel } =
    useNotificationSoundSettings();

  const { settings: browserSettings, updateSettings: updateBrowserSettings } =
    useBrowserNotificationSettings();
  const browserSupported = isBrowserNotificationSupported();
  // Live permission: stays in sync if the user changes it in browser settings.
  const { permission, refresh: refreshPermission } = useNotificationPermission();
  const [showUnblockHelp, setShowUnblockHelp] = useState(false);
  // Effective state: a stored "enabled" only counts if the browser still grants it.
  const browserEnabled = browserSettings.enabled && permission === "granted";

  // The toggle click is the user gesture that lets us request OS permission.
  const handleBrowserMasterToggle = async () => {
    if (browserSettings.enabled) {
      updateBrowserSettings({ enabled: false });
      return;
    }
    let nextPermission = permission;
    if (nextPermission !== "granted") {
      nextPermission = await requestNotificationPermission();
      refreshPermission();
    }
    updateBrowserSettings({ enabled: nextPermission === "granted" });
  };

  const queryClient = useQueryClient();

  const toggleSoundMutation = useMutation({
    mutationFn: settingsApi.toggleNotificationsSound,

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-preferences"] });
    },

    onError: () =>
      toast.error(
        "An error occured while updating user prefernces, please try again."
      ),
  });

  return (
    <div className="divide-y divide-white/10">
      {browserSupported && (
        <div className={SECTION}>
          <div>
            <h2 className="text-base/7 font-semibold text-white">
              Browser Notifications
            </h2>
            <p className="mt-1 text-sm/6 text-gray-400">
              Show desktop pop-ups for alerts while Newsquawk is open in a tab.
              Watchlist pop-ups are enabled per watchlist (tick &ldquo;Pop-Up&rdquo;)
              and follow the Watchlist Alert Mode below.
            </p>
          </div>
          <div className={CONTROL_COL}>
            <SettingToggleRow
              label="Enable browser notifications"
              checked={browserEnabled}
              onChange={handleBrowserMasterToggle}
              disabled={permission === "denied"}
              srLabel="Enable browser notifications"
            />

            {permission === "denied" && (
              <div className="text-sm text-amber-400">
                <p>
                  Notifications are blocked in your browser.{" "}
                  <button
                    type="button"
                    onClick={() => setShowUnblockHelp((v) => !v)}
                    className="underline text-squawk-blue hover:text-squawk-blue/80"
                  >
                    {showUnblockHelp ? "Hide steps" : "How to unblock"}
                  </button>
                </p>
                {showUnblockHelp && (
                  <ol className="mt-2 ml-4 list-decimal space-y-1 text-gray-400">
                    <li>
                      Click the site-settings icon (the sliders or padlock) at
                      the left of the address bar.
                    </li>
                    <li>
                      Find &ldquo;Notifications&rdquo; and set it to
                      &ldquo;Allow&rdquo; (or reset the permission).
                    </li>
                    <li>
                      Come back here &mdash; this toggle re-enables automatically;
                      click it to turn pop-ups on.
                    </li>
                  </ol>
                )}
              </div>
            )}

            <SettingToggleRow
              label="New headlines"
              checked={browserSettings.headlines}
              onChange={(v) => updateBrowserSettings({ headlines: v })}
              disabled={!browserEnabled}
              srLabel="Browser notifications for new headlines"
            />
            <SettingToggleRow
              label="Truth Social"
              checked={browserSettings.truthSocial}
              onChange={(v) => updateBrowserSettings({ truthSocial: v })}
              disabled={!browserEnabled}
              srLabel="Browser notifications for Truth Social"
            />
          </div>
        </div>
      )}

      <div className={SECTION}>
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Headlines Notification Sound
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Enable/Disable sound notifications for new headlines received.
          </p>
        </div>
        <div className={CONTROL_COL}>
          <SettingToggleRow
            label="Play a sound for new headlines"
            checked={notificationsSound.headlines}
            onChange={() =>
              toggleSoundMutation.mutate({
                headlines: !notificationsSound.headlines,
                truth_social: notificationsSound.truth_social,
              })
            }
            srLabel="Enable/Disable headlines notification sound"
          />
          <ChannelSoundPicker
            value={soundSettings.headlines}
            onChange={(patch) => updateChannel("headlines", patch)}
            disabled={!notificationsSound.headlines}
          />
        </div>
      </div>

      <div className={SECTION}>
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Truth Social Notification Sound
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Enable/Disable sound notifications for new truth social stories
            received.
          </p>
        </div>
        <div className={CONTROL_COL}>
          <SettingToggleRow
            label="Play a sound for Truth Social"
            checked={notificationsSound.truth_social}
            onChange={() =>
              toggleSoundMutation.mutate({
                headlines: notificationsSound.headlines,
                truth_social: !notificationsSound.truth_social,
              })
            }
            srLabel="Enable/Disable Truth Social notification sound"
          />
          <ChannelSoundPicker
            value={soundSettings.truthSocial}
            onChange={(patch) => updateChannel("truthSocial", patch)}
            disabled={!notificationsSound.truth_social}
          />
        </div>
      </div>

      <TruthSocialNotificationControls
        disabled={!notificationsSound.truth_social}
      />

      <div className={SECTION}>
        <div>
          <h2 className="text-base/7 font-semibold text-white">
            Watchlist Alert Mode
          </h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            Configure when watchlist alerts (sound and pop-up) are triggered.
            Requires sound or pop-up to be enabled on individual watchlists.
          </p>
        </div>
        <div className={CONTROL_COL}>
          <RadioGroup
            value={watchlistAlertMode}
            onChange={(value: WatchlistAlertMode) => setWatchlistAlertMode(value)}
            className="space-y-2"
          >
            {alertModeOptions.map((option) => (
              <RadioGroup.Option
                key={option.value}
                value={option.value}
                className={({ checked }) =>
                  `relative flex cursor-pointer rounded-lg px-4 py-3 focus:outline-none ${
                    checked
                      ? "bg-squawk-blue/20 border border-squawk-blue"
                      : "bg-white/5 border border-white/10 hover:bg-white/10"
                  }`
                }
              >
                {({ checked }) => (
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center">
                      <div className="text-sm">
                        <RadioGroup.Label
                          as="p"
                          className={`font-medium ${
                            checked ? "text-white" : "text-gray-300"
                          }`}
                        >
                          {option.label}
                          {option.value === "off" && (
                            <span className="ml-2 text-xs text-gray-500">
                              (Default)
                            </span>
                          )}
                        </RadioGroup.Label>
                        <RadioGroup.Description
                          as="span"
                          className="text-gray-500"
                        >
                          {option.description}
                        </RadioGroup.Description>
                      </div>
                    </div>
                    {checked && (
                      <div className="shrink-0 text-squawk-blue">
                        <svg
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                )}
              </RadioGroup.Option>
            ))}
          </RadioGroup>
          <div>
            <p className="mb-2 text-sm/6 text-gray-400">Watchlist alert sound</p>
            <ChannelSoundPicker
              value={soundSettings.portfolio}
              onChange={(patch) => updateChannel("portfolio", patch)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Notifications;
